import type { ReactNode } from 'react';

export type TimelineItemStatus = 'done' | 'inProgress' | 'pending' | 'failed';

export interface TimelineItem {
  id: string;
  status: TimelineItemStatus;
  title: string;
  subtitle?: string;
  timestamp?: string;
  meta?: ReactNode;
}

interface TimelineProps {
  items: TimelineItem[];
  emptyMessage?: string;
  className?: string;
}

const STATUS_DOT_CLASS: Record<TimelineItemStatus, string> = {
  done: 'bg-[var(--success-500)] border-[var(--success-500)]',
  inProgress: 'bg-[var(--warning-500)] border-[var(--warning-500)] animate-pulse',
  pending: 'bg-[var(--gray-100)] border-[var(--gray-300)]',
  failed: 'bg-[var(--error-500)] border-[var(--error-500)]',
};

const STATUS_ICON: Record<TimelineItemStatus, string> = {
  done: 'pi pi-check',
  inProgress: 'pi pi-spin pi-spinner',
  pending: '',
  failed: 'pi pi-times',
};

const STATUS_TEXT_COLOR: Record<TimelineItemStatus, string> = {
  done: 'text-[var(--gray-900)]',
  inProgress: 'text-[var(--gray-900)]',
  pending: 'text-[var(--gray-400)]',
  failed: 'text-[var(--error-700)]',
};

const STATUS_LABEL: Record<TimelineItemStatus, string> = {
  done: 'concluído',
  inProgress: 'em andamento',
  pending: 'pendente',
  failed: 'falhou',
};

/**
 * Timeline vertical com nós conectados, ícones por status.
 *
 * Cont-4c: usada na tela /uad/contratos/{id}/status-sync para mostrar
 * jornada do contrato (cadastrado → aguardando-sync → vigente).
 *
 * @example
 * <Timeline items={[
 *   { id: '1', status: 'done', title: 'Cadastrado', timestamp: '14:25:00' },
 *   { id: '2', status: 'inProgress', title: 'Sincronizando RM' },
 *   { id: '3', status: 'pending', title: 'Vigente' },
 * ]} />
 */
export default function Timeline({
  items,
  emptyMessage = 'Nenhum evento registrado.',
  className = '',
}: TimelineProps) {
  if (items.length === 0) {
    return (
      <p className={`text-sm text-[var(--gray-500)] ${className}`}>{emptyMessage}</p>
    );
  }

  return (
    <ol className={`space-y-0 ${className}`} aria-live="polite">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const nextStatus = items[index + 1]?.status;
        const connectorColor =
          item.status === 'done' && nextStatus === 'done'
            ? 'border-[var(--success-500)]'
            : item.status === 'failed'
              ? 'border-[var(--error-500)]'
              : item.status === 'inProgress'
                ? 'border-[var(--warning-500)]'
                : 'border-dashed border-[var(--gray-300)]';

        return (
          <li key={item.id} className="relative flex gap-4 pb-6 last:pb-0">
            {/* Linha vertical conectora */}
            {!isLast && (
              <span
                className={`absolute left-3 top-7 -bottom-1 w-0 border-l-2 ${connectorColor}`}
                aria-hidden="true"
              />
            )}

            {/* Nó */}
            <span
              className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 ${STATUS_DOT_CLASS[item.status]}`}
              role="img"
              aria-label={`${item.title} — ${STATUS_LABEL[item.status]}`}
            >
              {STATUS_ICON[item.status] && (
                <i className={`${STATUS_ICON[item.status]} text-[10px] text-white`} />
              )}
            </span>

            {/* Conteúdo */}
            <div className={`min-w-0 flex-1 ${STATUS_TEXT_COLOR[item.status]}`}>
              <p className="text-sm font-medium">{item.title}</p>
              {item.subtitle && (
                <p className="mt-0.5 text-xs opacity-80">{item.subtitle}</p>
              )}
              {item.timestamp && (
                <p className="mt-0.5 font-mono text-xs opacity-70">{item.timestamp}</p>
              )}
              {item.meta && <div className="mt-1 text-xs">{item.meta}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
